import React from 'react';

const CONSOLIDATED_ID = '__consolidated__';

const fmtLastRun = (iso) => {
  if (!iso) return 'never';
  try {
    // Server persists last_run as UTC ISO. Show in IST for the operator.
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false });
  } catch (_e) { return iso; }
};

const SchedulerPanel = ({ schedulerStatus, schedulerHour, setSchedulerHour, loading, emails,
  selectedPlant, onEnable, onDisable, onTestEmail, onTriggerNow }) => {
  const isConsolidated = selectedPlant?.id === CONSOLIDATED_ID;
  const targetKey = isConsolidated ? 'consolidated' : selectedPlant?.id;
  const targetEnabled = Boolean(schedulerStatus?.targets?.[targetKey]);
  const anyEnabled = Boolean(schedulerStatus?.enabled);

  return (
    <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Daily Email Scheduler</h2>
      <p className="text-sm text-gray-600 mb-4 sm:mb-6">
        Toggle per recipient group — enabling <strong>Consolidated</strong> and enabling an individual plant are independent.
      </p>

      <div className={`p-3 sm:p-4 rounded-lg border-2 mb-4 ${targetEnabled ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-300'}`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-base sm:text-lg break-words" data-testid="scheduler-target-status">
              Status for <span className="text-[#156ACA]">{selectedPlant?.name}</span>: {targetEnabled ? 'Enabled' : 'Disabled'}
            </h3>
            {schedulerStatus?.next_run && (
              <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">Next scheduled run: {schedulerStatus.next_run}</p>
            )}
            <p className="text-xs sm:text-sm text-gray-600 mt-1" data-testid="scheduler-last-run">
              Last send: <strong>{fmtLastRun(schedulerStatus?.last_run)}</strong> (IST)
            </p>
            {!schedulerStatus?.smtp_configured && (
              <p className="text-xs sm:text-sm text-orange-600 mt-1">SMTP not configured. Add SMTP settings below.</p>
            )}
            {schedulerStatus?.targets && Object.keys(schedulerStatus.targets).length > 0 && (
              <p className="text-[11px] sm:text-xs text-gray-500 mt-1 break-words" data-testid="scheduler-enabled-targets">
                Currently enabled: {Object.entries(schedulerStatus.targets).filter(([, v]) => v).map(([k]) => k).join(', ') || 'none'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {targetEnabled ? (
              <button
                onClick={onDisable}
                className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 px-4 min-h-[44px] rounded-lg transition-colors flex-1 md:flex-none"
                data-testid="disable-scheduler-btn"
              >
                Disable {isConsolidated ? 'Consolidated' : 'this Plant'}
              </button>
            ) : (
              <>
                <select
                  value={schedulerHour}
                  onChange={(e) => setSchedulerHour(parseInt(e.target.value))}
                  className="px-3 py-2.5 min-h-[44px] border border-gray-300 rounded-lg text-sm"
                  data-testid="scheduler-hour-select"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:00 IST</option>
                  ))}
                </select>
                <button
                  onClick={onEnable}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 px-4 min-h-[44px] rounded-lg transition-colors flex-1 md:flex-none"
                  data-testid="enable-scheduler-btn"
                >
                  Enable {isConsolidated ? 'Consolidated' : 'this Plant'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {anyEnabled && (
        <div className="p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="min-w-0">
              <h4 className="font-semibold text-amber-900">Missed today's send?</h4>
              <p className="text-xs sm:text-sm text-amber-800">
                Manually fire the daily job now for every currently-enabled target.
              </p>
            </div>
            <button
              onClick={onTriggerNow}
              disabled={loading || !schedulerStatus?.smtp_configured}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5 px-4 min-h-[44px] rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
              data-testid="trigger-now-btn"
            >
              {loading ? 'Sending...' : 'Send Now'}
            </button>
          </div>
        </div>
      )}

      <div className="p-3 sm:p-4 bg-[#E8F2FC] rounded-lg border border-[#156ACA]/20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="min-w-0">
            <h4 className="font-semibold">Test Email</h4>
            <p className="text-xs sm:text-sm text-gray-600 break-words">
              Send a test report for <strong>{selectedPlant?.name}</strong> to configured emails
            </p>
          </div>
          <button
            onClick={onTestEmail}
            disabled={loading || !emails.length}
            className="bg-[#156ACA] hover:bg-[#0F5299] text-white font-semibold py-2.5 px-4 min-h-[44px] rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
            data-testid="test-email-btn"
          >
            {loading ? 'Sending...' : 'Send Test Email'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchedulerPanel;
